/**
 * PayPalCheckout — PayPal Smart Buttons for the public event checkout (REQ-045).
 *
 * Contract posture:
 * - Three-op flow through the typed facade (lib/payments-api): createOrder →
 *   buyer approval → SERVER capture (+ status polling for async captures) —
 *   unlike Stripe there is a separate server-side capture step.
 * - The FE never computes/sends money and never calls actions.order.patch() /
 *   onShippingChange to change money — price + fee are 100% server-derived;
 *   every amount rendered comes from an op response (amountMinor/currency).
 * - `orderAccessToken` lives in a ref, is sent only to the capture/status ops,
 *   and is NEVER logged.
 * - The PayPal JS SDK loads with client-id / merchant-id / BN code / currency
 *   from the event's public payment config — nothing hardcoded. If the real
 *   SDK can't load (offline preview harness), a mock "PayPal" button drives the
 *   SAME op flow, so the wiring is identical when the real SDK is present.
 * - No raw card inputs — PayPal owns its popup.
 */
import { useEffect, useRef, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { useT } from "@v0y4ge/i18n-runtime";
import {
  captureEventPayPalOrder, createEventPayPalOrder, getEventPayPalOrderStatus,
  newIdempotencyKey, formatMinor, type PayPalOrderResult, type PayPalSdkConfig,
} from "../../lib/payments-api";

interface PayPalButtonsHandle { render: (el: HTMLElement) => Promise<void>; close?: () => void }
interface PayPalSdk {
  Buttons: (opts: {
    createOrder: () => Promise<string>;
    onApprove: (data: { orderID?: string }) => Promise<void>;
    onError?: (err: unknown) => void;
    onCancel?: () => void;
    style?: Record<string, string | number>;
  }) => PayPalButtonsHandle;
}

const sdkPromises = new Map<string, Promise<PayPalSdk | null>>();
function sdkKey(cfg: PayPalSdkConfig): string { return `${cfg.clientId}|${cfg.merchantId}|${cfg.currency}`; }
/** Retry support: drop the cached (failed) load so the next attempt re-fetches. */
function clearPayPalSdkCache(cfg: PayPalSdkConfig): void { sdkPromises.delete(sdkKey(cfg)); }
/** EXPLICIT preview flag, set ONLY by the preview harness (paypal-preview-seeds.js).
 *  Live pages never set it — an SDK-load failure there is surfaced as unavailable,
 *  NEVER a fake button (defect fix 2026-07-24). */
function previewMockAllowed(): boolean { return (window as unknown as { __CD_PREVIEW_PAYPAL_MOCK__?: boolean }).__CD_PREVIEW_PAYPAL_MOCK__ === true; }
/** Load the PayPal JS SDK once per client config. Resolves null when unreachable. */
function loadPayPalSdk(cfg: PayPalSdkConfig): Promise<PayPalSdk | null> {
  const key = sdkKey(cfg);
  const existing = sdkPromises.get(key);
  if (existing) return existing;
  const p = new Promise<PayPalSdk | null>((resolve) => {
    const w = window as unknown as { paypal?: PayPalSdk };
    if (w.paypal?.Buttons) { resolve(w.paypal); return; }
    const q = new URLSearchParams({ "client-id": cfg.clientId, currency: cfg.currency, intent: "capture", components: "buttons" });
    if (cfg.merchantId) q.set("merchant-id", cfg.merchantId);
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?${q.toString()}`;
    if (cfg.bnCode) s.setAttribute("data-partner-attribution-id", cfg.bnCode);
    const to = window.setTimeout(() => resolve(null), 6000);
    s.onload = () => { window.clearTimeout(to); resolve((window as unknown as { paypal?: PayPalSdk }).paypal ?? null); };
    s.onerror = () => { window.clearTimeout(to); resolve(null); };
    document.head.appendChild(s);
  });
  sdkPromises.set(key, p);
  return p;
}

const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

export interface PaidOrder extends PayPalOrderResult { orderAccessToken: string }

export function PayPalCheckout({ eventId, ticketId, quantity, sdkConfig, onPaid }: {
  eventId: string;
  ticketId?: string;
  quantity?: number;
  sdkConfig: PayPalSdkConfig;
  /** Called once the capture is terminal-"paid"; awaited (registration). */
  onPaid: (order: PaidOrder) => Promise<void>;
}) {
  const t = useT();
  const [sdkMode, setSdkMode] = useState<"pending" | "real" | "mock" | "unavailable">("pending");
  const [sdkEpoch, setSdkEpoch] = useState(0); // bump → clear cache + reload SDK
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "error" | "info"; text: string } | null>(null);
  const [order, setOrder] = useState<PayPalOrderResult | null>(null);
  const accessRef = useRef<{ orderId: string; token: string } | null>(null); // token never logged
  const hostRef = useRef<HTMLDivElement | null>(null);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const createOrder = async (): Promise<string> => {
    // Fresh idempotency key per attempt; the server derives amount/currency.
    const r = await createEventPayPalOrder({ eventId, ticketId, quantity, idempotencyKey: newIdempotencyKey() });
    accessRef.current = { orderId: r.orderId, token: r.orderAccessToken };
    if (aliveRef.current) setOrder({ orderId: r.orderId, status: r.status, amountMinor: r.amountMinor, currency: r.currency });
    return r.orderId;
  };

  /** Server capture, then poll to a terminal status; hand off to onPaid. */
  const settle = async (): Promise<void> => {
    const a = accessRef.current;
    if (!a) throw new Error("no-order");
    let r = await captureEventPayPalOrder(eventId, a.orderId, a.token);
    for (let i = 0; i < 8 && r.status === "pending"; i++) {
      await wait(1200);
      r = await getEventPayPalOrderStatus(eventId, a.orderId, a.token);
    }
    if (aliveRef.current) setOrder(r);
    if (r.status !== "paid") {
      const err = new Error(r.status === "canceled" ? "canceled" : "not-paid") as Error & { terminal?: string };
      err.terminal = r.status;
      throw err;
    }
    try {
      await onPaid({ ...r, orderAccessToken: a.token });
    } catch {
      // Payment captured but the follow-on registration failed — be precise.
      throw Object.assign(new Error("registered-failed"), { afterPayment: true, orderId: a.orderId });
    }
  };

  const approve = async () => {
    if (!aliveRef.current) return;
    setBusy(true); setNotice(null);
    try {
      await settle();
    } catch (e) {
      if (!aliveRef.current) return;
      const x = e as { afterPayment?: boolean; orderId?: string; terminal?: string; message?: string };
      setNotice({
        tone: "error",
        text: x.afterPayment
          ? `Your payment went through, but the registration didn't complete. Nothing to pay again — contact the organizer with reference ${x.orderId}.`
          : x.terminal === "canceled"
            ? "The payment was canceled before it completed. You can try again whenever you're ready."
            : "The payment didn't complete — you haven't been charged. Please try again.",
      });
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  // Load the SDK from the config; mount real Smart Buttons when available.
  useEffect(() => {
    let disposed = false;
    let handle: PayPalButtonsHandle | null = null;
    setSdkMode("pending");
    // Fake-button guard: mock mode ONLY behind the explicit harness flag.
    const failMode = previewMockAllowed() ? "mock" as const : "unavailable" as const;
    loadPayPalSdk(sdkConfig).then((sdk) => {
      if (disposed || !aliveRef.current) return;
      if (!sdk) { setSdkMode(failMode); return; }
      setSdkMode("real");
      // Buttons mount on the next paint, once the host div exists.
      window.setTimeout(() => {
        if (disposed || !hostRef.current) return;
        handle = sdk.Buttons({
          style: { layout: "vertical", label: "paypal" }, // ui-config — PayPal SDK enum value, not copy
          createOrder,
          onApprove: () => approve(),
          onCancel: () => { if (aliveRef.current) setNotice({ tone: "info", text: "Payment canceled — you haven't been charged." }); },
          onError: () => { if (aliveRef.current) setNotice({ tone: "error", text: "PayPal couldn't start the payment. Please try again." }); },
        });
        handle.render(hostRef.current).catch(() => { if (aliveRef.current) setSdkMode(failMode); });
      }, 0);
    });
    return () => { disposed = true; try { handle?.close?.(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkConfig.clientId, sdkConfig.merchantId, sdkConfig.currency, sdkEpoch]);

  // Mock button (preview harness, SDK unreachable): same create → capture flow.
  const mockPay = async () => {
    if (busy) return;
    setBusy(true); setNotice(null);
    try { await createOrder(); } catch {
      if (aliveRef.current) { setNotice({ tone: "error", text: "Couldn't start the PayPal payment. Please try again." }); setBusy(false); }
      return;
    }
    await approve();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {order ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12.5, color: T.n2 }}>
          <span>{t("landing.payPalCheckout.text4", "Order total (confirmed by PayPal)")}</span>
          <b style={{ color: T.n1, fontSize: 14 }}>{formatMinor(order.amountMinor, order.currency)}</b>
        </div>
      ) : null}
      {sdkMode === "pending" ? (
        <div style={{ height: 45, borderRadius: 4, background: T.n6, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: T.soft }}>
          {t("landing.payPalCheckout.text5", "You will be redirected to PayPal to finish paying.")}</div>
      ) : sdkMode === "real" ? (
        <div ref={hostRef} aria-busy={busy || undefined} />
      ) : sdkMode === "unavailable" ? (
        <div role="alert" style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", background: alpha(T.red1, 7), border: `1px solid ${alpha(T.red1, 30)}`, borderRadius: 6 }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.5, color: T.n1 }}>{t("landing.payPalCheckout.text6", "PayPal is temporarily unavailable — please try again. You haven't been charged.")}</div>
          <Btn size="sm" variant="secondary" style={{ alignSelf: "flex-start" }}
            onClick={() => { clearPayPalSdkCache(sdkConfig); setNotice(null); setSdkEpoch((e) => e + 1); }}>
            {t("landing.payPalCheckout.text7", "Pay with PayPal")}</Btn>
        </div>
      ) : (
        /* ui-raw: customer-facing checkout action filled with the tenant/provider brand colour — Studio Button must not render on the paid checkout surface */
        <button
          onClick={busy ? undefined : () => { void mockPay(); }}
          aria-disabled={busy || undefined}
          data-testid="lp-signup-paypal-button"
          style={{ height: 45, borderRadius: 4, border: "none", background: "#FFC439", color: "#003087", fontWeight: 800, fontStyle: "italic", fontSize: 16, letterSpacing: -0.2, cursor: busy ? "wait" : "pointer", fontFamily: "inherit", opacity: busy ? 0.75 : 1 }}>
          {busy ? t("landing.payPalCheckout.text8", "Completing your payment…") : t("landing.payPalCheckout.text9", "PayPal")}
        </button>
      )}
      {busy && sdkMode === "real" ? <div style={{ fontSize: 12, color: T.soft }}>{t("landing.payPalCheckout.text8", "Completing your payment…")}</div> : null}
      {notice ? (
        <div role={notice.tone === "error" ? "alert" : "note"} style={{ fontSize: 12.5, lineHeight: 1.5, color: notice.tone === "error" ? T.red1 : T.n2, background: notice.tone === "error" ? alpha(T.red1, 7) : T.n6, border: `1px solid ${notice.tone === "error" ? alpha(T.red1, 30) : T.border}`, borderRadius: 6, padding: "8px 12px" }}>
          {notice.text}
        </div>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.soft }}>
        <Icon.lock size={11} /> {t("landing.payPalCheckout.text10", "Secured by PayPal")}</div>
    </div>
  );
}
